import { LitElement, css, html } from 'lit'

import '../catalog/ct-program-card.js'
import { getProgramByKey } from '../../data/programs/index.js'
import { getCatalogPrograms } from '../../services/catalog-programs.js'
import { getProgramTerms } from '../../services/program-terms.js'
import {
  buildCatalogProgram,
  inferContinent,
} from '../../utils/catalog-data.js'
import { getRecommendedPrograms } from '../../utils/program-recommendations.js'

export class CtRecommendedPrograms extends LitElement {
  static properties = {
    currentProgram: { type: String, attribute: 'current-program' },
    limit: { type: Number },
    recommendations: { state: true },
    loading: { state: true },
    error: { state: true },
  }

  constructor() {
    super()
    this.currentProgram = ''
    this.limit = 3
    this.recommendations = []
    this.loading = true
    this.error = ''
    this.programs = []
    this.sourceProgram = null
    this.loadRequest = 0
  }

  static styles = css`
    :host {
      display: block;
      width: min(100%, 1200px);
      margin: 0 auto;
      color: #52666c;
      font-family: Poppins, Arial, sans-serif;
    }

    * { box-sizing: border-box; }

    .grid {
      display: grid;
      width: 100%;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 24px;
    }

    ct-program-card {
      width: 100%;
      min-width: 0;
      max-width: 384px;
    }

    .status {
      margin: 0;
      color: #66777c;
      font-size: 15px;
      line-height: 22px;
    }

    @media (min-width: 768px) and (max-width: 1199px) {
      :host { width: 94%; }

      .grid {
        grid-template-columns: repeat(2, minmax(0, 480px));
        justify-content: start;
      }

      ct-program-card { max-width: 480px; }
    }

    @media (max-width: 767px) {
      :host { width: 94%; }

      .grid {
        grid-template-columns: minmax(0, 480px);
        justify-content: start;
      }

      ct-program-card { max-width: 480px; }
    }
  `

  connectedCallback() {
    super.connectedCallback()
    this.loadPrograms()
  }

  updated(changedProperties) {
    if (
      changedProperties.has('currentProgram') &&
      changedProperties.get('currentProgram') !== undefined
    ) {
      this.loadPrograms()
      return
    }

    if (changedProperties.has('limit')) {
      this.updateRecommendations()
    }
  }

  async loadPrograms() {
    const request = ++this.loadRequest
    this.loading = true
    this.error = ''
    this.sourceProgram = null

    try {
      const configuredProgram = getProgramByKey(this.currentProgram)
      const sourceRequest = configuredProgram
        ? getProgramTerms(configuredProgram.programKey)
            .catch(() => null)
        : Promise.resolve(null)
      const [result, sourceResult] = await Promise.all([
        getCatalogPrograms(),
        sourceRequest,
      ])
      if (request !== this.loadRequest) return

      this.programs = result.data
      if (sourceResult?.program && sourceResult.terms.length > 0) {
        this.sourceProgram = buildCatalogProgram(
          sourceResult.program,
          sourceResult.terms
        )
      }
      this.error = result.data.length === 0 && result.errors.length > 0
        ? 'Nie udało się teraz pobrać polecanych programów.'
        : ''
      this.updateRecommendations()
    } catch {
      if (request !== this.loadRequest) return
      this.programs = []
      this.recommendations = []
      this.error = 'Nie udało się teraz pobrać polecanych programów.'
    } finally {
      if (request === this.loadRequest) this.loading = false
    }
  }

  updateRecommendations() {
    const configuredProgram = getProgramByKey(this.currentProgram)
    const sourceFallback = this.sourceProgram ?? (configuredProgram
      ? {
          programKey: configuredProgram.programKey,
          continent: inferContinent(
            configuredProgram.catalog?.destinations?.[0],
            configuredProgram.catalog?.continent
          ),
          travelStyles: configuredProgram.catalog?.travelStyles ?? [],
          durationDays: null,
          priceFrom: null,
          terms: Object.values(configuredProgram.termDetails ?? {})
            .map(term => ({ group: term.group }))
            .filter(term => term.group),
        }
      : null)

    this.recommendations = getRecommendedPrograms(
      this.programs,
      this.currentProgram,
      this.limit,
      sourceFallback
    )
  }

  render() {
    if (this.loading) {
      return html`<p class="status" role="status">Ładujemy polecane programy…</p>`
    }

    if (this.error && this.recommendations.length === 0) {
      return html`<p class="status" role="status">${this.error}</p>`
    }

    if (this.recommendations.length === 0) return ''

    return html`
      <div class="grid">
        ${this.recommendations.map(program => html`
          <ct-program-card .program=${program}></ct-program-card>
        `)}
      </div>
    `
  }
}

if (!customElements.get('ct-recommended-programs')) {
  customElements.define(
    'ct-recommended-programs',
    CtRecommendedPrograms
  )
}
